import React from 'react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet-async';
import PageHero from '@components/hero';
import Heading from '@components/pageHeading';
import { SectionHeading } from '@components/Common';
import { Layout, Container } from 'layouts';

import MigrationService from '@constants/techStack';

// Assets
import headerImage from '@assets/services/tech-stack-mig/tech-stack-header.jpg';

import CloudMigIcon from '@assets/services/tech-stack-mig/cloud-mig.svg';
import DigitalMigIcon from '@assets/services/tech-stack-mig/digital-migration.svg';
import EnterpriseMigIcon from '@assets/services/tech-stack-mig/enterprise-migration.svg';
import WebAppMigIcon from '@assets/services/tech-stack-mig/web-app-migrationt.svg';
import DatabaseMigIcon from '@assets/services/tech-stack-mig/database-mig.svg';
import DataMigIcon from '@assets/services/tech-stack-mig/data-mig.svg';
import AppMigIcon from '@assets/services/tech-stack-mig/app-mig.svg';

import AnalyzingIcon from '@assets/services/tech-stack-mig/analyzing-app.svg';
import MigRiskIcon from '@assets/services/tech-stack-mig/mig-risks.svg';
import PlanningIcon from '@assets/services/tech-stack-mig/planning-starting-migration.svg';
import ProgressControlIcon from '@assets/services/tech-stack-mig/progress-control.svg';
import NewAppIcon from '@assets/services/tech-stack-mig/implementing-new-app.svg';

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-flow: row wrap;
  margin-top: 30px;
`;

const ServiceIconElem = styled.div`
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: start;
  flex-flow: column;
  padding: 15px 20px;
  flex: 0 0 33.33%;
  @media (max-width: ${props => props.theme.breakpoints.l}) {
    flex: 0 0 100%;
  }
  svg {
    margin-bottom: 20px;
  }
`;

const Content = styled.div`
  font-weight: 500;
  font-size: 14px;
`;

const BgWrapper = styled.div`
  background-color: ${props => props.theme.colors.primary.lighter};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-flow: column wrap;
  max-width: 500px;
  margin: 0 auto;
  &.inline {
    max-width: none;
    display: inline-flex;
    margin: 0;
    flex: 0 0 50%;
    padding: 0 20px;
    @media (max-width: ${props => props.theme.breakpoints.s}) {
      flex: 0 0 100%;
    }
  }
`;

const ListItem = styled.div`
  font-size: 14px;
  margin: 5px;
  font-weight: 600;
  &::before {
    content: ' ';
    width: 10px;
    height: 10px;
    display: inline-flex;
    margin-right: 15px;
    border-radius: 50%;
    background-color: ${props => props.theme.colors.primary.base};
  }
`;

const ItemTitle = styled.p`
  font-size: 20px;
  font-weight: 600;
`;

const SubHeading = styled.p`
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const ProcessItemWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  justify-content: center;
`;

const ProcessItem = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 70%;
  margin-bottom: 70px;
  @media (max-width: ${props => props.theme.breakpoints.s}) {
    width: 100%;
  }
  svg {
    margin-bottom: 20px;
  }
`;

const Label = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const MigServices = styled(Container)`
  padding-top: 0;
  display: flex;
  height: fit-content;
  flex-flow: row wrap;
  align-items: start;
  justify-content: center;
`;

const TechStackMigrationService = () => {
  const iconMap = {
    cloudmig: <CloudMigIcon />,
    digitalmig: <DigitalMigIcon />,
    enterprisemig: <EnterpriseMigIcon />,
    webappmig: <WebAppMigIcon />,
    databasemig: <DatabaseMigIcon />,
    datamig: <DataMigIcon />,
    applicationmig: <AppMigIcon />,
  };
  return (
    <Layout>
      <Helmet title={'Tech Stack Migration'} />
      <PageHero image={headerImage} />
      <Heading title="Tech Stack Migration Service" noHeadingIcon={true}>
        <p>
          We know what a big and crucial decision it is to migrate your
          platform. We know it takes a heartful of determination to make a move
          like this. The thought is scary when you are afraid of things like
          what if the new tech will be hard to manage or what if there's
          downtime at the peak hours.
        </p>

        <p>
          The courage, it takes to move on from something you've been relying
          upon since ages to something new and in-trend, though seems logical,
          is very tough for the heart to take. And we understand that feeling.
          With decades of experience in tech stack migration, we feel like we
          can play the role of a therapist who, with you, can direct your
          business in the right direction with all your required criteria about
          how you'd like to move forward and what all technology you'd like to
          move on to.
        </p>
        <p>
          Our team of experts know critical times like this. With our unique
          algorithms, we move your tech without and hassle and without or with
          very minimal downtime.
        </p>
        <p>
          While deciding this, we often forget what all positives are we going
          to achieve. For instance, moving your tech stack to the new one is
          going to lower the maintenance cost, the new system would be more
          efficient, performance would be increased, the application would be
          more scalable, you won't suffer data losses, won't suffer from
          technical limitations, etc. and these are just a few out of many
          advantages you get after getting your tech stack upgraded.
        </p>
        <p>
          We will assist you at every step of this process, from choosing the
          technology that is closely aligned to your long-term business vision
          to maintaining it after its implementation. At CodeMyMobile, we are
          having a mighty exposure in this service sector over the decades, this
          becomes naturally easier for us to advise the tech-stack that are most
          fitting for your enterprise.
        </p>
      </Heading>
      <Container>
        <SectionHeading>Pros of tech stack migration</SectionHeading>
        <ListWrapper>
          <ListItem>Lower Maintenance Cost</ListItem>
          <ListItem>More Efficient</ListItem>
          <ListItem>Higher Performance</ListItem>
          <ListItem>Scalability</ListItem>
          <ListItem>Reliable</ListItem>
          <ListItem>Easy Adoption to changes</ListItem>
          <ListItem>Data Protection & Security</ListItem>
        </ListWrapper>
      </Container>
      <BgWrapper>
        <Container>
          <SectionHeading>Our staple migration services</SectionHeading>
          <SubHeading>
            From backend to frontend, and beyond that, we migrate it all
          </SubHeading>
          <IconWrapper>
            {MigrationService.map((item, id) => {
              return (
                <ServiceIconElem key={id}>
                  {iconMap[item.type]}
                  <ItemTitle>{item.title}</ItemTitle>
                  <Content>{item.content}</Content>
                </ServiceIconElem>
              );
            })}
          </IconWrapper>
        </Container>
      </BgWrapper>
      <Container>
        <SectionHeading>Our working process</SectionHeading>
        <ProcessItemWrapper>
          <ProcessItem>
            <AnalyzingIcon />
            <Label>Analyzing the current state of your application</Label>
          </ProcessItem>
          <ProcessItem>
            <MigRiskIcon />
            <Label>
              Creating a map of risks related to migration and preparing
              possible solutions
            </Label>
          </ProcessItem>
          <ProcessItem>
            <PlanningIcon />
            <Label>Planning and starting work related to migration</Label>
          </ProcessItem>
          <ProcessItem>
            <ProgressControlIcon />
            <Label>Progress control</Label>
          </ProcessItem>
          <ProcessItem>
            <NewAppIcon />
            <Label>Implementing and maintaining the new app</Label>
          </ProcessItem>
        </ProcessItemWrapper>
      </Container>
      <BgWrapper>
        <SectionHeading>Our migration services</SectionHeading>
        <SubHeading>
          With our migration services, CodeMyMobile has successfully helped more
          than a hundred companies migrate smoothly.
        </SubHeading>
        <MigServices>
          <ListWrapper className="inline">
            <ListItem>Migration from one technology to another</ListItem>
            <ListItem>Mobile-friendliness Enablement</ListItem>
            <ListItem>Product Deployment and Setup</ListItem>
            <ListItem>UI upgrading</ListItem>
            <ListItem>Software Migration Services</ListItem>
            <ListItem>Product Customization</ListItem>
          </ListWrapper>
          <ListWrapper className="inline">
            <ListItem>Integration of third-party APIs</ListItem>
            <ListItem>Infrastructure re-designing</ListItem>
            <ListItem>Knowledge transfer and configuration</ListItem>
            <ListItem>Security and Quality Assurance</ListItem>
            <ListItem>
              Employee training for using the software, applications, and
              digital products
            </ListItem>
            <ListItem>Software or API Integration</ListItem>
          </ListWrapper>
        </MigServices>
      </BgWrapper>
    </Layout>
  );
};

export default TechStackMigrationService;
